import { useState, useEffect } from "react";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import QuestionForm from "./QuestionForm";
import { getRequest, putRequest } from "../../../../config";

const InvestorLeadSection = () => {
    const [section, setSection] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedIndex, setExpandedIndex] = useState(null);
    const [savingIds, setSavingIds] = useState(new Set());
    const [isSectionExpanded, setIsSectionExpanded] = useState(true);

    useEffect(() => {
        fetchSectionData();
    }, []);

    const fetchSectionData = async () => {
        setLoading(true);
        const response = await getRequest('/modules/investor-lead/section');
        if (response?.body) {
            setSection(response.body.section || null);
            setQuestions(response.body.questions || []);
        }
        setLoading(false);
    };

    const handleUpdate = (index, updatedQuestion) => {
        setQuestions(prev => {
            const next = [...prev];
            next[index] = updatedQuestion;
            return next;
        });
    };

    const handleSave = async (index) => {
        const question = questions[index];
        if (!question._id) return;

        setSavingIds(prev => new Set(prev).add(question._id));

        const payload = {
            _id: question._id,
            qDescription: question.qDescription,
            questionType: question.questionType,
            customOptions: question.customOptions,
            predefinedOptionsInfo: question.predefinedOptionsInfo,
            dateInfo: question.dateInfo,
            tooltip: question.tooltip,
            toolboxOffer: question.toolboxOffer,
            riskScores: question.riskScores,
            riskDescription: question.riskDescription,
            recommendDescription: question.recommendDescription,
            attachmentRequired: question.attachmentRequired,
            tags: question.tags,
        };

        const response = await putRequest('/modules/investor-lead/questions', payload);

        setSavingIds(prev => {
            const next = new Set(prev);
            next.delete(question._id);
            return next;
        });

        if (response?.code === 200 && response.body) {
            setQuestions(prev => {
                const next = [...prev];
                next[index] = response.body;
                return next;
            });
        }
    };

    if (loading) {
        return (
            <div className="tac p3rem">
                <p className="fs0-9rem c15 op60">Loading Investor Lead section...</p>
            </div>
        );
    }

    if (!section) {
        return (
            <div className="br12px p3rem tac" style={{
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                border: '2px dashed #dee2e6'
            }}>
                <p className="fs1-1rem fw500" style={{ color: '#495057', margin: '0 0 0.5rem 0' }}>
                    Section DEPF008 not found
                </p>
                <p className="fs0-9rem" style={{ color: '#6c757d', margin: 0 }}>
                    No section with sectionNo &quot;DEPF008&quot; exists in the database
                </p>
            </div>
        );
    }

    return (
        <div className="bg1 br12px mb1-5rem" style={{ border: '1px solid #e8e8e8', minWidth: 0, maxWidth: '100%' }}>
            {/* Section Header */}
            <div
                className="df jcsb aic cp"
                style={{
                    padding: '1.5rem 2rem',
                    background: isSectionExpanded
                        ? 'linear-gradient(135deg, #4CAF5015 0%, #2196F315 100%)'
                        : 'white',
                    borderBottom: isSectionExpanded ? '1px solid #e8e8e8' : 'none',
                    borderRadius: isSectionExpanded ? '12px 12px 0 0' : '12px',
                    transition: 'all 0.3s ease'
                }}
                onClick={() => setIsSectionExpanded(prev => !prev)}
            >
                <div className="df aic" style={{ gap: '12px', minWidth: 0 }}>
                    <button
                        type="button"
                        className="df aic jcc cp"
                        style={{
                            border: 'none',
                            background: isSectionExpanded ? '#4CAF50' : '#e0e0e0',
                            color: 'white',
                            padding: '8px',
                            borderRadius: '8px',
                            minWidth: '36px',
                            minHeight: '36px',
                            transition: 'all 0.2s ease',
                            flexShrink: 0
                        }}
                    >
                        {isSectionExpanded
                            ? <ExpandLess style={{ fontSize: '20px' }} />
                            : <ExpandMore style={{ fontSize: '20px' }} />
                        }
                    </button>

                    <div>
                        <div className="df aic" style={{ gap: '8px', flexWrap: 'wrap' }}>
                            <span
                                className="fs0-8rem fw600"
                                style={{
                                    background: '#4CAF5020',
                                    color: '#2e7d32',
                                    padding: '3px 10px',
                                    borderRadius: '6px',
                                    fontFamily: 'monospace'
                                }}
                            >
                                {section.sectionNo}
                            </span>
                            <span className="fs1rem fw600 c15">
                                {section.sectionName || 'Investor Lead Introduction'}
                            </span>
                        </div>
                        <p className="fs0-8rem c15 op60 m0 mt0-25rem">
                            {questions.length} question{questions.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                </div>
            </div>

            {/* Questions */}
            {isSectionExpanded && (
                <div className="p1-5rem" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {questions.length === 0 ? (
                        <div className="tac p2rem" style={{ color: '#6c757d' }}>
                            <p className="fs0-9rem m0">No questions found for this section.</p>
                        </div>
                    ) : (
                        questions.map((question, index) => (
                            <div key={question._id || index}>
                                <QuestionForm
                                    question={question}
                                    index={index}
                                    moduleKey="DEPF"
                                    sectionIndex={0}
                                    isExpanded={expandedIndex === index}
                                    onToggleExpand={() => setExpandedIndex(expandedIndex === index ? null : index)}
                                    onUpdate={(updated) => handleUpdate(index, updated)}
                                    onRemove={() => {}}
                                    hideDelete
                                />
                                {expandedIndex === index && (
                                    <div className="df jcfe" style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
                                        <button
                                            type="button"
                                            onClick={() => handleSave(index)}
                                            disabled={savingIds.has(question._id)}
                                            style={{
                                                border: 'none',
                                                background: savingIds.has(question._id) ? '#aaa' : '#4CAF50',
                                                color: 'white',
                                                padding: '10px 24px',
                                                borderRadius: '8px',
                                                fontSize: '0.9rem',
                                                fontWeight: '500',
                                                cursor: savingIds.has(question._id) ? 'not-allowed' : 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            {savingIds.has(question._id) ? 'Saving...' : 'Save Question'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default InvestorLeadSection;
